import * as THREE from "three";

// `diag.vrDebug` (see Utils/Diagnostics/Diagnostics.ts): read-only inspection helpers for the live VR/LHQ three.js scene,
// usable from the devtools console or an agent's browser session. Results are plain JSON-serializable objects,
// except the `live*` getters, which return the actual three.js objects.
// Relies on `g._3JSState` (the latest R3F state; set by LHQ/Scene.tsx).

type AnyMat = THREE.Material & Partial<THREE.MeshBasicMaterial & THREE.MeshStandardMaterial>;

function State() {
	const s = g._3JSState as {scene: THREE.Scene, camera: THREE.Camera, gl: THREE.WebGLRenderer}|n;
	if (s?.scene == null) throw new Error("[vrDebug] no live three.js state (g._3JSState); is the VR page mounted?");
	return s;
}
const Mats = (m: THREE.Mesh)=>(Array.isArray(m.material) ? m.material : [m.material]) as AnyMat[];
const R = (n: number, d = 3)=>Math.round(n * 10 ** d) / 10 ** d;
const V3 = (v: THREE.Vector3)=>[R(v.x), R(v.y), R(v.z)];

function TexInfo(t: THREE.Texture|n) {
	if (t == null) return null;
	const img = t.image as {width?: number, height?: number}|n;
	const w = img?.width, h = img?.height;
	return {
		name: t.name || null, w, h,
		// block-compressed textures need dims divisible by 4, else they can sample as black
		dimsOk: w != null && h != null && w % 4 == 0 && h % 4 == 0,
		channel: t.channel, colorSpace: t.colorSpace, format: t.format, mips: t.mipmaps?.length ?? 0,
		minFilter: t.minFilter, wrapS: t.wrapS,
	};
}

function MatInfo(m: AnyMat) {
	return {
		name: m.name, type: m.type,
		color: m.color?.getHexString() ?? null,
		opacity: m.opacity, transparent: m.transparent, side: m.side,
		map: TexInfo(m.map), lightMap: TexInfo(m.lightMap), lightMapIntensity: m.lightMapIntensity ?? null,
		envMap: m.envMap != null, emissive: m.emissive?.getHexString() ?? null,
	};
}

function LightmapKey(o: THREE.Object3D) {
	for (let n: THREE.Object3D|n = o; n != null; n = n.parent) if (typeof n.userData?.lm == "string") return n.userData.lm as string;
	return null;
}

function MeshInfo(m: THREE.Mesh, detail = false) {
	const g = m.geometry;
	const box = new THREE.Box3().setFromObject(m);
	const base = {
		name: m.name, parent: m.parent?.name ?? null, visible: m.visible,
		lm: LightmapKey(m),
		mats: Mats(m).map(a=>`${a.name} (${a.type}${a.lightMap ? ", lm" : ""})`),
		worldMin: V3(box.min), worldMax: V3(box.max),
	};
	if (!detail) return base;
	return {
		...base,
		attributes: Object.fromEntries(Object.entries(g.attributes).map(([k, a])=>[k, {count: a.count, itemSize: a.itemSize}])),
		uvRange: Object.fromEntries(["uv", "uv1"].filter(k=>g.attributes[k]).map(k=>{
			const a = g.attributes[k] as THREE.BufferAttribute; let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
			for (let i = 0; i < a.count; i++) { const x = a.getX(i), y = a.getY(i); x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
			return [k, [R(x0), R(y0), R(x1), R(y1)]];
		})),
		materials: Mats(m).map(MatInfo),
		userData: m.userData,
	};
}

function AllMeshes() {
	const out = [] as THREE.Mesh[];
	State().scene.traverse(o=>{ if ((o as THREE.Mesh).isMesh) out.push(o as THREE.Mesh); });
	return out;
}
const Match = (m: THREE.Mesh, filter?: string|RegExp)=>{
	if (filter == null) return true;
	const re = typeof filter == "string" ? new RegExp(filter, "i") : filter;
	return re.test(m.name) || re.test(m.parent?.name ?? "") || Mats(m).some(a=>re.test(a.name));
};

export const vrDebug = {
	/** Overview: camera pose, renderer info, mesh/material counts. */
	summary() {
		const {camera, gl} = State();
		const meshes = AllMeshes();
		const types = {} as Record<string, number>;
		for (const m of meshes) for (const a of Mats(m)) types[a.type] = (types[a.type] ?? 0) + 1;
		return {
			camera: {pos: V3(camera.getWorldPosition(new THREE.Vector3())), dir: V3(camera.getWorldDirection(new THREE.Vector3()))},
			xrPresenting: gl.xr?.isPresenting ?? false,
			render: {calls: gl.info.render.calls, triangles: gl.info.render.triangles, textures: gl.info.memory.textures, geometries: gl.info.memory.geometries},
			meshCount: meshes.length, materialTypes: types,
		};
	},
	/** Brief info for meshes whose name / parent name / material name matches `filter` (string = case-insensitive regex). */
	meshes(filter?: string|RegExp, limit = 200) {
		return AllMeshes().filter(m=>Match(m, filter)).slice(0, limit).map(m=>MeshInfo(m));
	},
	/** Detailed info (attributes, uv ranges, materials + textures, userData) for matching meshes. */
	mesh(filter: string|RegExp, limit = 10) {
		return AllMeshes().filter(m=>Match(m, filter)).slice(0, limit).map(m=>MeshInfo(m, true));
	},
	/** Meshes likely to render (near-)black, each with the reasons found. */
	suspects() {
		const out = [] as {name: string, parent: string|n, reasons: string[]}[];
		for (const m of AllMeshes()) {
			if (!m.visible) continue;
			const reasons = [] as string[];
			for (const a of Mats(m)) {
				if (a.type != "MeshBasicMaterial") reasons.push(`${a.name}: realtime-lit ${a.type} (no baked lightmap => depends on the dim realtime lights)`);
				if (a.map && !TexInfo(a.map)!.dimsOk) reasons.push(`${a.name}: map dims ${TexInfo(a.map)!.w}x${TexInfo(a.map)!.h} not divisible by 4`);
				if (a.lightMap && !TexInfo(a.lightMap)!.dimsOk) reasons.push(`${a.name}: lightMap dims not divisible by 4`);
				if (a.lightMap && m.geometry.attributes.uv1 == null) reasons.push(`${a.name}: has lightMap but no uv1`);
				if (a.color && a.color.r + a.color.g + a.color.b < 0.05 && !a.map) reasons.push(`${a.name}: near-black color, no map`);
			}
			if (reasons.length) out.push({name: m.name, parent: m.parent?.name ?? null, reasons});
		}
		return out;
	},
	/** Live objects (non-serializable), for manual poking in the console. */
	get liveScene() { return State().scene; },
	get liveCamera() { return State().camera; },
	get liveRenderer() { return State().gl; },
	liveMeshes(filter?: string|RegExp) { return AllMeshes().filter(m=>Match(m, filter)); },
};
